import {
  Cancel as CancelIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Sms as SmsIcon,
  TaskAlt as TaskAltIcon,
  ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';

import { useGetLiveLeadsQuery } from 'src/api/k8sQuery';
import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  refreshDurations,
  ToolbarRefreshButton,
} from 'src/app/modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { endingDate } from 'src/recoil/atoms/endingDate';
import { startingDate } from 'src/recoil/atoms/startingDate';
import { IUserAtom, user } from 'src/recoil/atoms/user';

// Status configuration with colors, labels, and icons
const STATUS_CONFIG = {
  ROUTED: {
    label: 'Routed',
    color: '#1976d2',
    bgColor: '#e3f2fd',
    description: 'Lead has been routed and is awaiting agent notification.',
    icon: SendIcon,
  },
  CONTACTED: {
    label: 'Agent Contacted',
    color: '#4a3cc4',
    bgColor: '#e9e4ff',
    description: 'SMS sent to agent with the lead details.',
    icon: SmsIcon,
  },
  ACCEPTED: {
    label: 'Agent Accepted',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    description: 'Agent accepted lead (replied YES).',
    icon: ThumbUpIcon,
  },
  CONNECTED: {
    label: 'Connected',
    color: '#1565c0',
    bgColor: '#e3f2fd',
    description: 'Call made to the lead (dialing or connected).',
    icon: PhoneIcon,
  },
  OUTCOME_SENT: {
    label: 'Outcome Sent',
    color: '#a06a00',
    bgColor: '#fff0d9',
    description: 'Outcome link sent to lead.',
    icon: TaskAltIcon,
  },
  AGENT_REJECTED: {
    label: 'Agent Rejected',
    color: '#8d6e63',
    bgColor: '#efebe9',
    description: 'Agent declined the lead.',
    icon: CancelIcon,
  },
  OUTCOME_COLLECTED: {
    label: 'Outcome Collected',
    color: '#00796b',
    bgColor: '#e0f2f1',
    description: 'Agent submitted the call outcome.',
    icon: TaskAltIcon,
  },
  EXPIRED: {
    label: 'Expired',
    color: '#3b2c1a',
    bgColor: '#eeeeee',
    description: 'Lead offer expired (no response in time).',
    icon: ScheduleIcon,
  },
  FAILED: {
    label: 'Failed',
    color: '#c62828',
    bgColor: '#ffebee',
    description: 'Lead was declined or auto-rejected.',
    icon: CancelIcon,
  },
};

export const AIOLeadFlow = () => {
  const userAtomLoadable = useRecoilValueLoadable(user);

  // Get date range from Recoil atoms (synced with URL query params)
  const startDate = useRecoilValue(startingDate);
  const endDate = useRecoilValue(endingDate);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<
    'createDate' | 'updateDate' | 'contactName' | 'status'
  >('updateDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [contactNameFilter, setContactNameFilter] = useState<string>('');
  const [contactPhoneFilter, setContactPhoneFilter] = useState<string>('');
  const [agentPhoneFilter, setAgentPhoneFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<
    | 'ROUTED'
    | 'CONTACTED'
    | 'ACCEPTED'
    | 'CONNECTED'
    | 'OUTCOME_SENT'
    | 'AGENT_REJECTED'
    | 'OUTCOME_COLLECTED'
    | 'EXPIRED'
    | 'FAILED'
    | ''
  >('');
  const businessId =
    userAtomLoadable?.state === 'hasValue'
      ? (userAtomLoadable?.contents as IUserAtom)?.businessid
      : undefined;

  const { data, isLoading, isFetching, refetch } = useGetLiveLeadsQuery(
    {
      start: startDate?.format('YYYY-MM-DD'),
      end: endDate?.format('YYYY-MM-DD'),
      businessId,
    },
    {
      skip: !businessId,
    }
  );

  const { leads, totalFilteredCount } = useMemo(() => {
    if (!data?.leads) return { leads: [], totalFilteredCount: 0 };

    let filtered = [...data.leads];

    if (contactNameFilter) {
      const lowerFilter = contactNameFilter.toLowerCase();
      filtered = filtered.filter((lead) =>
        lead.contactName?.toLowerCase().includes(lowerFilter)
      );
    }

    if (contactPhoneFilter) {
      filtered = filtered.filter((lead) =>
        lead.contactPhoneNumber?.includes(contactPhoneFilter)
      );
    }

    if (agentPhoneFilter) {
      filtered = filtered.filter((lead) =>
        lead.agentPhoneNumber?.includes(agentPhoneFilter)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortBy) {
        case 'contactName':
          aVal = a.contactName?.toLowerCase() || '';
          bVal = b.contactName?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'updateDate':
        default:
          aVal = a.updateDate ? new Date(a.updateDate).getTime() : 0;
          bVal = b.updateDate ? new Date(b.updateDate).getTime() : 0;
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    const totalFilteredCount = filtered.length;

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedLeads = filtered.slice(startIndex, endIndex).map((lead) => ({
      ...lead,
      id: lead.intentId, // DataTable requires unique id
    }));

    return { leads: paginatedLeads, totalFilteredCount };
  }, [
    data,
    contactNameFilter,
    contactPhoneFilter,
    agentPhoneFilter,
    statusFilter,
    sortBy,
    sortOrder,
    page,
    pageSize,
  ]);

  // When date range changes, reset to first page
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [contactNameFilter, contactPhoneFilter, agentPhoneFilter, statusFilter]);

  // Helper to format US phone numbers like (XXX) XXX-XXXX
  const formatPhoneNumber = (value?: string | null): string => {
    if (!value) return 'N/A';
    const raw = String(value);
    const digits = raw.replace(/\D/g, '');
    const normalized =
      digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (normalized.length === 10) {
      return `(${normalized.slice(0, 3)}) ${normalized.slice(
        3,
        6
      )}-${normalized.slice(6)}`;
    }
    return raw; // fallback to original if non-10-digit
  };

  // Toolbar component using shared EnhancedTableToolbar with refresh button
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        hideSearch={true}
        hideCsvExportButton={true}
        csvExport={false}
        hideScaleLoader={true}
        hideVerticalDivider={true}
        leftComponent={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              label="Contact Name"
              value={contactNameFilter}
              onChange={(e) => setContactNameFilter(e.target.value)}
            />
            <TextField
              size="small"
              label="Contact Phone"
              value={contactPhoneFilter}
              onChange={(e) => setContactPhoneFilter(e.target.value)}
            />
            <TextField
              size="small"
              label="Agent Phone"
              value={agentPhoneFilter}
              onChange={(e) => setAgentPhoneFilter(e.target.value)}
            />
            <Divider orientation="vertical" flexItem />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    (e.target.value as
                      | 'ROUTED'
                      | 'CONTACTED'
                      | 'ACCEPTED'
                      | 'CONNECTED'
                      | 'OUTCOME_SENT'
                      | 'AGENT_REJECTED'
                      | 'OUTCOME_COLLECTED'
                      | 'EXPIRED'
                      | 'FAILED'
                      | '') || ''
                  )
                }
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([key, status]) => (
                  <MenuItem key={key} value={key}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ScaleLoader
              loading={isLoading || isFetching}
              height={15}
              width={1}
            />
          </Box>
        }
        rightComponent={
          <ToolbarRefreshButton
            id="AIOLeadFlow"
            duration={refreshDurations.dur30sec}
            onClickForRefresh={() => {
              refetch();
            }}
            isLoading={isLoading || isFetching}
          />
        }
      />
    ),
    [
      contactNameFilter,
      contactPhoneFilter,
      agentPhoneFilter,
      statusFilter,
      refetch,
      isLoading,
      isFetching,
    ]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  // Column definitions
  const columns: IColDef[] = [
    {
      field: 'updateDate',
      headerName: 'Updated',
      width: 180,
      valueFormatter: (params) =>
        params.value
          ? DateTime.fromISO(params.value).toFormat('MM/dd/yy hh:mm a')
          : '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const config =
          STATUS_CONFIG[params.value as keyof typeof STATUS_CONFIG];

        if (!config) return null;

        // const StatusIcon = config.icon;

        return (
          <Tooltip title={params.row?.statusDetail || config.description} arrow>
            <Chip
              label={config.label}
              size="small"
              sx={{
                backgroundColor: config.bgColor,
                color: config.color,
                fontWeight: 600,
                fontSize: '0.8125rem',
                // borderLeft: `3px solid ${config.color}`,
                borderRadius: '16px',
                px: 1.25,
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'contactName',
      headerName: 'Contact',
      width: 180,
      valueFormatter: (params) => params.value || '',
    },
    {
      field: 'contactPhoneNumber',
      headerName: 'Contact Phone',
      width: 150,
      valueFormatter: (params) => formatPhoneNumber(params.value),
      enableSorting: false,
    },
    {
      field: 'agentPhoneNumber',
      headerName: 'Agent Phone',
      width: 150,
      valueFormatter: (params) => formatPhoneNumber(params.value),
      enableSorting: false,
    },
    {
      field: 'routingRule',
      headerName: 'Routing Rule',
      width: 250,
      enableSorting: false,
      renderCell: (params: GridRenderCellParams) => {
        const text: string = params.value || 'N/A';
        const needsTruncate = typeof text === 'string' && text.length > 100;
        const display = needsTruncate ? `${text.slice(0, 100)}…` : text;
        return (
          <Tooltip
            title={needsTruncate ? text : ''}
            arrow
            slotProps={{ tooltip: { sx: { fontSize: '0.95rem' } } }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {display}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      {isLoading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <DataTable
            rows={leads}
            columns={columns}
            initialState={{
              sorting: {
                sortModel: [{ field: 'updateDate', sort: 'desc' }],
              },
            }}
            ToolbarComponent={ToolbarComponent}
            paginationMode="client"
            page={page}
            pageSize={pageSize}
            rowCount={totalFilteredCount}
            onPageChange={(newPage) => handlePageChange(newPage)}
            onPageSizeChange={(newSize) => handlePageSizeChange(newSize)}
            onSortModelChange={(model) => {
              const first = model?.[0];
              if (first) {
                const allowed: Record<
                  string,
                  'createDate' | 'updateDate' | 'contactName' | 'status'
                > = {
                  createDate: 'createDate',
                  updateDate: 'updateDate',
                  contactName: 'contactName',
                  status: 'status',
                };
                setSortBy(allowed[first.field] ?? 'updateDate');
                setSortOrder(first.sort ?? 'desc');
              }
            }}
          />
        </Paper>
      )}

      {data && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {totalFilteredCount !== data.totalCount
              ? `Showing ${leads.length.toLocaleString()} of ${totalFilteredCount.toLocaleString()} filtered (${data.totalCount.toLocaleString()} total) from ${startDate?.format(
                  'MM/DD/YY'
                )} to ${endDate?.format('MM/DD/YY')}`
              : `Showing ${leads.length.toLocaleString()} of ${data.totalCount.toLocaleString()} leads from ${startDate?.format(
                  'MM/DD/YY'
                )} to ${endDate?.format('MM/DD/YY')}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isFetching ? 'Updating…' : ''}
          </Typography>
        </Box>
      )}
    </>
  );
};
